import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Avatar, Box, Grid, Link, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import classNames from 'classnames';

import { EngagementHistory, EngagementHistoryFormat } from 'api';
import { getSubdomainUrl } from 'helpers';
import { useProtectedClient } from 'hooks';
import { PaddedPaper, SocialIcon } from 'components';

import { getEngagementType } from './helpers';
import ActionButton from './ActionButton';
import FollowConnectButton from './FollowConnectButton';

interface Props {
  format: EngagementHistoryFormat;
  item: EngagementHistory;
}

const useStyles = makeStyles(theme => ({
  root: {
    position: 'relative',
    height: 316,
  },
  container: {
    height: '100%',
  },
  network: {
    position: 'absolute',
    top: theme.spacing(2),
    left: theme.spacing(2),
  },
  avatar: {
    width: 100,
    height: 100,
  },
  dim: {
    opacity: 0.1,
  },
  bio: theme.mixins.clamp(2),
  buttonGroup: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: theme.spacing(2),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },
}));

const HistoryCard: FC<Props> = ({ format, item }) => {
  const [hovered, setHovered] = useState(false);
  const { t } = useTranslation();
  const client = useProtectedClient();
  const classes = useStyles();

  function handleMouseEnter() {
    if (format === EngagementHistoryFormat.Chronological) {
      setHovered(true);
    }
  }

  function handleMouseLeave() {
    if (format === EngagementHistoryFormat.Chronological) {
      setHovered(false);
    }
  }

  function renderName() {
    return (
      <Link
        display="block"
        target="_blank"
        href={item.network_profile_url}
        align="center"
        variant="h6"
        noWrap
      >
        {item.network_profile_name}
      </Link>
    );
  }

  function renderEngagements() {
    let engagementsChildren = null;

    if (format === EngagementHistoryFormat.Chronological) {
      engagementsChildren = getEngagementType(item.engagement_type);
    }

    if (format === EngagementHistoryFormat.Aggregate) {
      engagementsChildren = t('network:engagement', { count: item.total_engagements });
    }

    return (
      <Typography
        className={hovered ? classes.dim : ''}
        gutterBottom
        align="center"
        color="primary"
      >
        {engagementsChildren}
      </Typography>
    );
  }

  function renderBio() {
    const bioClasses = classNames(classes.bio, {
      [classes.dim]: hovered,
    });

    return (
      <Typography className={bioClasses} align="center" color="textSecondary">
        {item.network_bio}
      </Typography>
    );
  }

  function renderActions() {
    if (format === EngagementHistoryFormat.Aggregate) {
      return <FollowConnectButton item={item} />;
    }

    if (!hovered) {
      return <FollowConnectButton item={item} />;
    }

    const contentUrl = `${getSubdomainUrl(client.subdomain)}/content/${item.content_id}`;

    return (
      <Grid container direction="column" className={classes.buttonGroup} spacing={1}>
        {!!item.content_id && (
          <Grid item>
            <ActionButton href={contentUrl} text={t('network:viewPost')} />
          </Grid>
        )}
        {!!item.share_url && (
          <Grid item>
            <ActionButton href={item.share_url} text={t('network:viewShare')} />
          </Grid>
        )}
        <Grid item>
          <FollowConnectButton item={item} />
        </Grid>
      </Grid>
    );
  }

  return (
    <PaddedPaper
      className={classes.root}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <SocialIcon className={classes.network} provider={item.network} />
      <Grid container direction="column" justify="flex-end" className={classes.container}>
        <Grid item xs>
          <Box marginBottom={2} display="flex" justifyContent="center">
            <Link href={item.network_profile_url} target="_blank">
              <Avatar
                src={item.network_profile_image}
                alt={item.network_profile_name}
                className={classes.avatar}
              />
            </Link>
          </Box>
          {renderName()}
          {renderEngagements()}
          {renderBio()}
        </Grid>
        <Grid item>{renderActions()}</Grid>
      </Grid>
    </PaddedPaper>
  );
};

export default HistoryCard;
